import { SiteContext, useSiteContext } from "hooks/use-site";
import { WebsiteJsonLd } from "lib/json-ld";
import { Helmet } from "react-helmet";

import Layout from "components/Layout";
import Section from "components/Section";
import Container from "components/Container";
import SectionBackground from "components/SectionBackground";
import SectionContent from "components/SectionContent";
import SectionText from "components/SectionText";
import StockPrice from "components/StockPrice";
import useMedia from "hooks/use-media";
import SectionPartner from "components/SectionPartner";
import TextParagraph from "components/SectionText/TextParagraph";
import { getSiteData, helmetSettingsFromMetadata } from "lib/site";
import OperationalHighlight from "components/OperationalHighlight";
import styles from "styles/pages/Home.module.scss";
import {
  getAllCustomPostType,
  getBtcPrice,
  getLatestSplash,
  getMenuBylocationV2,
  getOperationalHighlight,
  getShowUi,
} from "data/fetchPlugin";
import { SITE_TITLE_EN } from "data/siteTitle";
import { useEffect, useState } from "react";
import { revalidateTime, toNumberFormat } from "lib/util";
import { defaultMetaSiteData } from "data/site";
import { getCookie, hasCookie } from "cookies-next";
import { useRouter } from "next/router";
import PopupModal from "components/PopupModal";
import moment from "moment";

export default function Home(props) {
  const router = useRouter();
  const splashCookie = hasCookie("user-click-splash");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // check splash data
    let redirectSplash = true;
    if (splashCookie)
      redirectSplash = getCookie("user-click-splash") === "YES" ? false : true;
    if (latestSplash && redirectSplash) {
      const check_cookie_not_splash = hasCookie(
        "user-not-click-splash-redirect-to-index",
      );
      if (check_cookie_not_splash) {
        const get_not_splash = getCookie(
          "user-not-click-splash-redirect-to-index",
        );
        const parse_date_time = JSON.parse(get_not_splash);
        const is_after_end_date = moment(parse_date_time.date_time).isAfter(
          latestSplash.ended_at,
        );
        if (!is_after_end_date) {
          router.push("/welcome");
        }
      } else {
        router.push("/welcome");
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [splashCookie]);

  const {
    ogTitle,
    ogDesc,
    operationalHighlights,
    news,
    showUi,
    metadata,
    locale,
    latestSplash,
    siteData,
  } = props;
  const site = useSiteContext({
    metadata,
    locale,
    siteData,
    showUi,
  });
  const isSm = useMedia("(max-width: 1023px)");
  const [btcPrice, setBtcPrice] = useState(0);

  let og = {};

  if (process.env.WORDPRESS_PLUGIN_SEO) {
    const ogImg = `/images/og-default-min.png`;
    const og_meta = metadata?.og ? metadata?.og : {}
    const newLocal = og = {
      ...metadata,
      twitter: {
        ...metadata?.twitter,
        title: ogTitle,
        description: ogDesc,
        imageUrl: ogImg,
      },
      og: {
        ...og_meta,
        title: ogTitle,
        description: ogDesc,
        imageUrl: ogImg,
      },
    };
  }

  const helmetSettings = helmetSettingsFromMetadata(og);

  useEffect(() => {
    (async () => {
      if (!process.env.HIDE_BTC_APPRE) {
        const data = await getBtcPrice();
        const price = `$${toNumberFormat(data?.usd || 0)}`;
        setBtcPrice(price);
      }
    })();
  }, []);

  const getIsShowSection = slug => {
    return typeof showUi?.status === "undefined"
      ? !Number(showUi?.find(u => u.slug === slug)?.is_show || true)
      : true;
  };

  return (
    !loading && (
      <SiteContext.Provider value={site}>
        <Layout isHomePage={true}>
          <Helmet {...helmetSettings} />
          <WebsiteJsonLd siteTitle={site?.siteData?.title || 'Jasmine Technology Solution'} />
          {/* Section: TopBanner */}
          <SectionContent
            style={{
              maxHeight: isSm ? "568px" : "100vh",
            }}>
            <SectionText section="top-content" pageTemplate="home" />
            <SectionBackground imageSrc="home" />
          </SectionContent>

          {/* Section: StockPrice */}
          {!getIsShowSection("HOME_STOCKPRICE") && (
            <Section>
              <Container>
                <StockPrice />
              </Container>
            </Section>
          )}

          {/* Section: About */}
          {!getIsShowSection("HOME_ABOUT") && (
            <SectionContent
              style={
                getIsShowSection("HOME_ABOUT")
                  ? { paddingTop: "0", display: "flex", alignItems: "center" }
                  : { paddingTop: "1rem" }
              }>
              <SectionText section="about-home" />
              <SectionBackground imageSrc="about" />
            </SectionContent>
          )}

          {/* Section: Operational Highlights */}
          {!getIsShowSection("HOME_OPERATIONAL_HIGHLIGHT") && (
            <SectionContent
              style={
                getIsShowSection("HOME_OPERATIONAL_HIGHLIGHT")
                  ? { paddingTop: "0", display: "flex", alignItems: "center" }
                  : { paddingTop: "1rem" }
              }>
              <OperationalHighlight items={operationalHighlights} />
            </SectionContent>
          )}

          {/* Section: Bitcoin */}
          {!getIsShowSection("HOME_WHY_BTC") && (
            <Section style={{ margin: 0 }}>
              <SectionText section="why-btc" />
            </Section>
          )}
          {/* Section: News */}
          {!getIsShowSection("HOME_NEWS") && news?.items?.length > 0 && (
            <Section style={{ margin: 0 }}>
              <SectionText
                section="news"
                datas={news?.items || []}
                options={news?.home_news_data || {}}
              />
            </Section>
          )}
          {/* Section: Bitcoin Appre */}
          {!getIsShowSection("HOME_BTC_APPRECIATES") && (
            <SectionContent style={{ paddingTop: "1rem" }}>
              <SectionText section="btc-appre" btcPrice={btcPrice} />
              <SectionBackground imageSrc="btc" />
            </SectionContent>
          )}
          {!getIsShowSection("HOME_PARTNER") && (
            <div className={styles.partners} style={{ marginTop: "1rem" }}>
              <TextParagraph className={styles.textHeader}>
                PARTNERS
              </TextParagraph>
              <SectionPartner />
            </div>
          )}
          {/* Load Popup Modal */}
          <PopupModal />
        </Layout>
      </SiteContext.Provider>
    )
  );
}

export async function getStaticProps(ctx) {
  const { locale } = ctx;
  let title = locale === "en" ? "Home" : "หน้าแรก";
  let ogTitle = `${title} - ${SITE_TITLE_EN}`;
  let ogDesc = `${SITE_TITLE_EN}`;
  const latestSplash = await getLatestSplash();
  const operationalHighlights = await getOperationalHighlight();
  const news = await getAllCustomPostType("news", locale, 3);
  const showUi = await getShowUi();
  const metadata = defaultMetaSiteData;
  const siteData = await getSiteData();

  return {
    props: {
      ogTitle,
      ogDesc,
      operationalHighlights,
      news,
      showUi,
      metadata,
      locale,
      latestSplash,
      siteData,
    },
    revalidate: revalidateTime(5),
  };
}
